import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO27018_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            ISO/IEC 27018 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Protection of Personally Identifiable Information (PII) in the Cloud
            — Code of practice for cloud service providers handling personal
            data.
          </p>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ISO/IEC 27018 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              ISO/IEC 27018 is an international standard for the protection of
              personally identifiable information (PII) in cloud computing
              environments. It provides guidelines and best practices for cloud
              service providers (CSPs) to manage and protect PII, ensuring
              compliance with data privacy regulations such as GDPR, CCPA, and
              other global data protection laws. ISO/IEC 27018 builds upon the
              ISO/IEC 27001 Information Security Management System (ISMS)
              framework, offering a specialized approach to safeguarding PII in
              the cloud.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Achieving ISO/IEC 27018 certification demonstrates a company’s
              commitment to data privacy, security, and transparency when
              handling personal data in cloud services. It is an essential
              certification for businesses seeking to gain trust from customers,
              mitigate privacy risks, and ensure compliance with the growing
              global emphasis on privacy protection.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISO/IEC 27018 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO/IEC 27018 is a specialized extension of ISO/IEC 27001,
              focusing on the protection of personally identifiable information
              (PII) within cloud environments. The standard provides guidelines
              and controls for cloud service providers (CSPs) and cloud service
              customers (CSCs) to ensure that PII is handled securely and in
              compliance with privacy regulations.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              ISO/IEC 27018 outlines the requirements for implementing the
              appropriate controls to protect personal data, ensuring that
              organizations:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Process and store personal data securely in cloud environments.",
                "Comply with privacy regulations, including GDPR and CCPA.",
                "Prevent unauthorized access to PII and ensure data integrity.",
                "Guarantee transparency in data processing and ensure customers are informed about how their personal information is managed.",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of ISO/IEC 27018 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "Cloud-Specific Privacy Controls",
                desc: "ISO/IEC 27018 provides cloud-specific controls and guidelines for managing the protection of personally identifiable information (PII). This includes data encryption, access controls, and data loss prevention measures that are specific to cloud environments.",
              },
              {
                icon: Globe,
                title: "Compliance with Data Protection Laws",
                desc: "The standard helps businesses comply with global privacy regulations such as GDPR, CCPA, and other data protection laws that require organizations to manage and secure personal data responsibly.",
              },
              {
                icon: Users,
                title: "Transparency and Accountability",
                desc: "ISO/IEC 27018 ensures that cloud providers are transparent about how they handle PII. The standard encourages businesses to provide clear data processing policies, ensuring that customers are informed about how their personal data is processed and used.",
              },
              {
                icon: Target,
                title: "Customer Consent Management",
                desc: "ISO/IEC 27018 emphasizes the importance of obtaining and managing customer consent for data processing. This is especially important for compliance with GDPR and other privacy regulations that mandate explicit consent for the collection and processing of personal data.",
              },
              {
                icon: BarChart3,
                title: "Risk Management",
                desc: "The standard requires businesses to assess the risks related to data breaches, unauthorized access, and data misuse in the cloud, ensuring that appropriate security controls are in place to mitigate these risks.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why ISO/IEC 27018 Certification Matters for Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Protection of Personally Identifiable Information (PII) — ISO/IEC 27018 ensures that PII stored and processed in cloud environments is handled with the utmost care and protected against unauthorized access, ensuring the privacy of individuals' personal data.",
                "Regulatory Compliance — Achieving ISO/IEC 27018 certification helps organizations comply with privacy regulations like GDPR, CCPA, and other global laws, reducing the risk of non-compliance penalties and enhancing customer trust.",
                "Customer Confidence — ISO/IEC 27018 certification demonstrates to customers that their personal data is managed securely and in compliance with privacy laws, enhancing customer trust and brand reputation.",
                "Data Security and Risk Mitigation — By following ISO/IEC 27018 guidelines, businesses can identify, assess, and mitigate security risks associated with cloud computing, such as data breaches, cyberattacks, and unauthorized access.",
                "Competitive Advantage — With growing concerns over data privacy, ISO/IEC 27018 certification gives organizations a competitive edge by signaling that they prioritize data protection in cloud environments, making them a preferred choice for customers who value privacy.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does ISO/IEC 27018 Certification Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              ISO/IEC 27018 includes a range of privacy-specific controls and
              guidelines for managing personally identifiable information (PII)
              in cloud environments. Key components include:
            </p>
            <div className="space-y-4">
              {[
                "1. Privacy Policies and Governance — Develop and implement clear privacy policies that outline how PII will be processed, stored, and protected in the cloud. This includes governance frameworks and defining roles and responsibilities for managing privacy controls.",
                "2. Data Encryption and Access Control — Ensure that PII is protected using data encryption, both in transit and at rest, and that access controls are implemented to restrict access to sensitive data to authorized personnel only.",
                "3. Transparency and Data Processing Agreements — Provide transparency in how personal data is processed in the cloud. This includes defining clear data processing agreements (DPAs) with cloud service providers, outlining their responsibilities regarding data protection and privacy.",
                "4. Customer Consent and Data Subject Rights — Implement processes to manage and track customer consent for data processing, and ensure that the organization upholds data subject rights, such as the right to access, rectification, erasure, and portability of personal data.",
                "5. Risk Management and Incident Response — Conduct regular risk assessments to identify and mitigate potential data security risks related to cloud services. Implement incident response plans to manage and respond to data breaches or privacy violations effectively.",
                "6. Monitoring, Auditing, and Continuous Improvement — Implement continuous monitoring, regular audits, and reporting mechanisms to ensure compliance with privacy policies and controls. The standard emphasizes continuous improvement to ensure that privacy practices evolve with changing regulations and emerging threats.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of ISO/IEC 27018 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Enhanced Data Privacy Protection",
                "Regulatory Compliance",
                "Increased Customer Trust",
                "Reduced Risk of Data Breaches",
                "Competitive Advantage",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Achieve ISO/IEC 27018 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              Achieving ISO/IEC 27018 certification involves the following key
              steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Define Privacy Policies and Governance — Establish a governance framework for privacy, outlining policies, roles, and responsibilities for managing personally identifiable information (PII) in the cloud.",
                "2. Conduct a Privacy Risk Assessment — Assess the privacy risks associated with processing personal data in cloud environments. Identify vulnerabilities and implement appropriate controls to mitigate these risks.",
                "3. Implement Data Security Controls — Apply encryption, access controls, and other security measures to protect PII stored and processed in the cloud.",
                "4. Create Data Processing Agreements — Define and document data processing agreements with cloud service providers, ensuring they meet the necessary privacy protection and security standards.",
                "5. Internal Audits and Documentation — Conduct internal audits to assess the effectiveness of your privacy controls and ensure they meet the requirements of ISO/IEC 27018.",
                "6. Third-Party Certification Audit — Engage a recognized certification body to conduct a third-party audit and verify compliance with ISO/IEC 27018.",
                "7. Ongoing Monitoring and Improvement — Implement regular monitoring and audits to assess the effectiveness of privacy controls and continuously improve data protection practices.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for ISO/IEC 27018 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. offers expert ISO/IEC 27018 certification
              consultancy services, helping businesses implement effective
              privacy information management systems (PIMS) in the cloud to
              ensure data protection and compliance with privacy regulations.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Gap Analysis and Risk Assessment",
                "Privacy Policy and Control Implementation",
                "Data Processing Agreement Development",
                "Internal Audit and Pre-certification Support",
                "Third-Party Certification Assistance",
                "Continuous Monitoring and Improvement",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses achieve ISO/IEC 27018 certification, ensuring
              cloud data privacy, security, and regulatory compliance.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO27018_Details;
